import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  ArrowRight,
  ArrowCounterClockwise,
  CalendarPlus,
  CheckCircle,
  Circle,
  CircleNotch,
  CreditCard,
  Globe,
  ListChecks,
  MapPin,
  PaintBrush,
  PaperPlaneTilt,
  Receipt,
  Rocket,
  ShieldCheck,
  Sparkle,
  Tag,
  Trophy,
  UserPlus,
  UsersThree,
  X,
  type Icon,
} from '@phosphor-icons/react';

import {
  dismissGettingStarted,
  getGettingStarted,
  markGettingStartedSeen,
  skipGettingStartedStep,
  type GettingStartedProgress,
  type GrowthFamily,
  type Milestone,
} from '@/api/gettingStarted';
import { Button } from '@/components/ui/button';
import { RowActions, type RowAction } from '@/components/ui/row-actions';

// Display metadata per setup milestone, keyed by the backend's stable wire key.
// `to` is the path suffix appended to /orgs/:slug (matches nav-items.ts).
// Exported so the SetupWizard (Home) reuses the same icons + deep-links.
export const MILESTONE_META: Record<string, { icon: Icon; to: string }> = {
  first_team: { icon: UsersThree, to: '/teams' },
  first_members: { icon: UserPlus, to: '/members' },
  import_locations: { icon: MapPin, to: '/schedule' },
  team_categories: { icon: Tag, to: '/settings/team-categories' },
  create_template: { icon: ListChecks, to: '/schedule?mode=template' },
  apply_schedule: { icon: CalendarPlus, to: '/schedule?mode=template' },
  branding: { icon: PaintBrush, to: '/settings/organization' },
  publish_website: { icon: Globe, to: '/website' },
  second_admin: { icon: ShieldCheck, to: '/settings/administrators' },
  connect_payments: { icon: CreditCard, to: '/settings/billing?tab=stripe' },
  first_invoice: { icon: Receipt, to: '/billing' },
  first_broadcast: { icon: PaperPlaneTilt, to: '/broadcasts' },
};

export const GETTING_STARTED_QUERY_KEY = (slug: string) => ['getting-started', slug];

export function useGettingStarted(slug: string) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  return useQuery({
    queryKey: GETTING_STARTED_QUERY_KEY(slug),
    queryFn: () => getGettingStarted(token, slug),
    enabled: !!token && !!slug,
    // While the onboarding member import is still running, poll so the
    // team/members/locations steps flip from "importing" to done on their own.
    refetchInterval: (query) => (query.state.data?.importing ? 3000 : false),
  });
}

// Steps populated by the onboarding member import — they show a loader while
// the import job is still running rather than an unchecked circle.
const IMPORT_CLUSTER = new Set(['first_team', 'first_members', 'import_locations']);

function formatTierValue(value: number, unit: GrowthFamily['unit'], locale: string): string {
  const n = unit === 'money_minor' ? Math.floor(value / 100) : value;
  return new Intl.NumberFormat(locale).format(n);
}

// rank orders setup milestones: completed (0, the user's recent wins) → unfinished
// core (1) → unfinished optional (2) → skipped (3). Stable sort keeps catalog
// order within a tier.
function rank(m: Milestone): number {
  if (m.done) return 0;
  if (m.skipped) return 3;
  return m.core ? 1 : 2;
}

// GettingStartedLauncher is a compact header control (next to the bell) that
// shows how many setup steps remain and opens the dialog from any page. Hidden
// once every core step is done.
export function GettingStartedLauncher({ slug }: { slug: string }) {
  const { t } = useTranslation('common');
  const [params, setParams] = useSearchParams();
  const query = useGettingStarted(slug);
  const data = query.data;
  if (!data || data.allCoreDone) return null;

  const remaining = Math.max(0, data.coreTotal - data.coreDone);
  return (
    <button
      type="button"
      onClick={() => {
        params.set('setup', '1');
        setParams(params);
      }}
      className="gs-nudge inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-pill border border-border px-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted sm:px-3"
    >
      <Rocket size={16} weight="bold" className="gs-nudge-icon shrink-0 text-primary" />
      <span>{t('gettingStarted.stepsLeft', { count: remaining })}</span>
    </button>
  );
}

// GettingStartedAchievements renders the inline achievements card on the
// dashboard once setup is complete (and celebrated).
export function GettingStartedAchievements({ slug }: { slug: string }) {
  const query = useGettingStarted(slug);
  const data = query.data;
  if (!data || !data.allCoreDone || data.celebrate) return null;
  return <AchievementsCard data={data} />;
}

// GettingStartedDialog is the centered setup modal. Rendered once at the shell
// level so it can be opened from any page (via the launcher's ?setup=1). It
// auto-opens once on the dashboard while setup is incomplete and not dismissed,
// and shows the one-time "club is live" celebration.
//
// sidebarCollapsed lets the dialog center over the CONTENT area (right of the
// desktop sidebar) rather than the whole viewport — without it the box looks
// shifted left against the content behind it. The full-screen overlay still
// dims everything; only the box shifts. Widths mirror DashboardShell's aside
// (260px expanded / 72px collapsed); keep them in sync.
export function GettingStartedDialog({
  slug,
  sidebarCollapsed = false,
}: {
  slug: string;
  sidebarCollapsed?: boolean;
}) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const forceOpen = params.get('setup') === '1';

  const query = useGettingStarted(slug);
  const [open, setOpen] = useState(false);
  const didInit = useRef(false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: GETTING_STARTED_QUERY_KEY(slug) });
  const seenMutation = useMutation({
    mutationFn: () => markGettingStartedSeen(token, slug),
    onSuccess: invalidate,
  });
  const dismissMutation = useMutation({
    mutationFn: () => dismissGettingStarted(token, slug),
    onSuccess: invalidate,
  });

  const data = query.data;

  // On Home the inline <SetupWizard> owns onboarding (incl. the celebration), so
  // this dialog no longer auto-opens there — it is purely launcher-driven now
  // (?setup=1) for reopening the checklist from any other page. didInit is kept
  // to avoid re-running the (now empty) init if data reference changes.
  useEffect(() => {
    if (didInit.current || !data) return;
    didInit.current = true;
  }, [data]);

  // The launcher (?setup=1) forces it open from any page.
  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);

  if (!data) return null;
  const { allCoreDone, celebrate } = data;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) return;
    if (celebrate) {
      seenMutation.mutate();
    } else if (!allCoreDone) {
      dismissMutation.mutate();
    }
    if (forceOpen) {
      params.delete('setup');
      setParams(params, { replace: true });
    }
  };

  const ordered = [...data.milestones].sort((a, b) => rank(a) - rank(b));
  const pct = data.coreTotal > 0 ? Math.round((data.coreDone / data.coreTotal) * 100) : 0;

  // Center over the content area, not the viewport: at lg+ the sidebar offsets
  // everything to the right, so shift the box by half the sidebar width. Both
  // literals are present so Tailwind's JIT generates them. Mobile (<lg) keeps
  // the plain viewport centering since the sidebar is hidden there.
  const lgOffset = sidebarCollapsed ? 'lg:left-[calc(50%+36px)]' : 'lg:left-[calc(50%+130px)]';

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[90] bg-foreground/40" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={`fixed left-1/2 ${lgOffset} top-1/2 z-[91] flex max-h-[82vh] w-[min(38rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-card text-foreground shadow-xl`}
        >
          {celebrate ? (
            <div className="flex flex-col items-center px-6 py-10 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/15">
                <Sparkle size={34} weight="fill" className="text-primary" />
              </div>
              <DialogPrimitive.Title className="mt-4 font-heading text-2xl font-bold tracking-tight">
                {t('gettingStarted.celebrate.title')}
              </DialogPrimitive.Title>
              <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                {t('gettingStarted.celebrate.body')}
              </p>
              <Button className="mt-6" onClick={() => handleOpenChange(false)} disabled={seenMutation.isPending}>
                {t('gettingStarted.celebrate.cta')}
              </Button>
            </div>
          ) : (
            <>
              <header className="flex items-start gap-3 border-b border-border px-6 py-4">
                <div className="flex-1">
                  <DialogPrimitive.Title className="font-heading text-lg font-bold leading-tight">
                    {t('gettingStarted.title')}
                  </DialogPrimitive.Title>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {t('gettingStarted.subtitle')}
                  </p>
                </div>
                <DialogPrimitive.Close asChild>
                  <Button type="button" variant="ghost" size="icon" aria-label={t('gettingStarted.dismiss')}>
                    <X size={18} />
                  </Button>
                </DialogPrimitive.Close>
              </header>

              <div className="flex items-center gap-3 px-6 pt-4">
                <div
                  className="h-2 flex-1 overflow-hidden rounded-pill bg-muted"
                  role="progressbar"
                  aria-valuenow={data.coreDone}
                  aria-valuemin={0}
                  aria-valuemax={data.coreTotal}
                >
                  <div
                    className="h-full rounded-pill bg-primary transition-[width] duration-500 motion-reduce:transition-none"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="shrink-0 font-heading text-sm font-semibold tabular-nums">
                  {t('gettingStarted.progress', { done: data.coreDone, total: data.coreTotal })}
                </span>
              </div>

              <ul className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-4 py-4">
                {ordered
                  .filter((m) => m.core)
                  .map((m) => (
                    <MilestoneRow
                      key={m.key}
                      milestone={m}
                      slug={slug}
                      importing={data.importing}
                      onChanged={invalidate}
                      onNavigate={() => setOpen(false)}
                    />
                  ))}
                {ordered.some((m) => !m.core) && (
                  <li className="px-2 pb-1 pt-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('gettingStarted.optionalSection')}
                  </li>
                )}
                {ordered
                  .filter((m) => !m.core)
                  .map((m) => (
                    <MilestoneRow
                      key={m.key}
                      milestone={m}
                      slug={slug}
                      importing={data.importing}
                      onChanged={invalidate}
                      onNavigate={() => setOpen(false)}
                    />
                  ))}
              </ul>
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function MilestoneRow({
  milestone,
  slug,
  importing,
  onChanged,
  onNavigate,
}: {
  milestone: Milestone;
  slug: string;
  importing: boolean;
  onChanged: () => void;
  onNavigate: () => void;
}) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const navigate = useNavigate();
  const token = auth.user?.access_token ?? null;
  const meta = MILESTONE_META[milestone.key];
  const skipMutation = useMutation({
    mutationFn: (skipped: boolean) => skipGettingStartedStep(token, slug, milestone.key, skipped),
    onSuccess: onChanged,
  });
  if (!meta) return null; // Unknown key (server ahead of bundle) — skip safely.

  const Glyph = meta.icon;
  const title = t(`gettingStarted.milestone.${milestone.key}.title`);
  const skipped = milestone.skipped && !milestone.done;
  // The onboarding import is still populating this step — show a loader instead
  // of an empty circle + actions, so a half-imported org doesn't look "unstarted".
  const loading = importing && !milestone.done && IMPORT_CLUSTER.has(milestone.key);

  // Per-row actions collapse into the design-system kebab (⋮) menu so the row
  // stays a single tidy line at every width — on a phone the old inline Skip +
  // Open buttons crushed the title/description down to "Ska…" / "Tillä…". The
  // menu is only meaningful while the step is actionable (not done / loading).
  let actions: RowAction[] = [];
  if (!loading && !milestone.done) {
    actions = skipped
      ? [
          {
            id: 'undo-skip',
            label: t('gettingStarted.undoSkip'),
            icon: ArrowCounterClockwise,
            onSelect: () => skipMutation.mutate(false),
            disabled: skipMutation.isPending,
          },
        ]
      : [
          {
            id: 'open',
            label: t('gettingStarted.cta'),
            icon: ArrowRight,
            onSelect: () => {
              onNavigate();
              navigate(`/orgs/${slug}${meta.to}`);
            },
          },
          {
            id: 'skip',
            label: t('gettingStarted.skip'),
            onSelect: () => skipMutation.mutate(true),
            disabled: skipMutation.isPending,
          },
        ];
  }

  return (
    <li className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-muted/60">
      {milestone.done ? (
        <CheckCircle size={22} weight="fill" className="shrink-0 text-primary" />
      ) : loading ? (
        <CircleNotch size={22} className="shrink-0 animate-spin text-primary motion-reduce:animate-none" />
      ) : (
        <Circle size={22} className={'shrink-0 ' + (skipped ? 'text-muted-foreground/50' : 'text-muted-foreground')} />
      )}
      <Glyph
        size={18}
        className={'shrink-0 ' + (milestone.done || skipped || loading ? 'text-muted-foreground' : 'text-foreground')}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <span
          className={
            'truncate text-sm font-medium ' +
            (milestone.done || skipped ? 'text-muted-foreground line-through' : 'text-foreground')
          }
        >
          {title}
        </span>
        {loading ? (
          <span className="truncate text-xs text-muted-foreground">{t('gettingStarted.importing')}</span>
        ) : (
          !milestone.done &&
          !skipped && (
            // Wraps on mobile (vertical space is cheap there) and truncates to a
            // single line from sm+ to keep desktop rows uniform.
            <span className="text-xs text-muted-foreground sm:truncate">
              {t(`gettingStarted.milestone.${milestone.key}.description`)}
            </span>
          )
        )}
      </div>

      {actions.length > 0 && (
        <RowActions menuActions={actions} triggerLabel={t('gettingStarted.rowMenu', { title })} className="shrink-0" />
      )}
    </li>
  );
}

function AchievementsCard({ data }: { data: GettingStartedProgress }) {
  const { t, i18n } = useTranslation('common');
  const [collapsed, setCollapsed] = useState(false);
  if (collapsed) return null;
  return (
    <section className="w-full rounded-2xl border border-border bg-card p-6 shadow-sm">
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15">
            <Trophy size={22} weight="fill" className="text-primary" />
          </div>
          <div className="flex flex-col">
            <h2 className="font-heading text-lg font-semibold tracking-tight">
              {t('gettingStarted.achievementsTitle')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('gettingStarted.pointsLabel', { points: data.pointsTotal })}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          aria-label={t('gettingStarted.dismiss')}
          className="rounded-pill p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X size={18} />
        </button>
      </header>
      <div className="mt-4 flex flex-col gap-4">
        {data.growth.map((g) => (
          <GrowthRow key={g.family} family={g} locale={i18n.language} />
        ))}
      </div>
    </section>
  );
}

function GrowthRow({ family, locale }: { family: GrowthFamily; locale: string }) {
  const { t } = useTranslation('common');
  const nextTier = family.tiers.find((tier) => !tier.earned);
  const earnedCount = family.tiers.filter((tier) => tier.earned).length;
  const pct = nextTier
    ? Math.min(100, Math.round((family.current / nextTier.threshold) * 100))
    : 100;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="font-medium">{t(`gettingStarted.growth.${family.family}`)}</span>
        <span className="tabular-nums text-muted-foreground">
          {formatTierValue(family.current, family.unit, locale)}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-pill bg-muted">
        <div
          className="h-full rounded-pill bg-primary transition-[width] duration-500 motion-reduce:transition-none"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {family.tiers.map((tier) => (
          <span
            key={tier.key}
            className={
              'inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-xs font-semibold ' +
              (tier.earned ? 'bg-primary/15 text-foreground' : 'bg-muted text-muted-foreground/70')
            }
          >
            <Trophy size={12} weight={tier.earned ? 'fill' : 'regular'} />
            {formatTierValue(tier.threshold, family.unit, locale)}
          </span>
        ))}
        <span className="ml-auto text-xs text-muted-foreground">
          {nextTier
            ? t('gettingStarted.growth.next', {
                value: formatTierValue(nextTier.threshold, family.unit, locale),
              })
            : t('gettingStarted.growth.maxed', { count: earnedCount })}
        </span>
      </div>
    </div>
  );
}
